import { useEffect, useState, useCallback } from "react";
import { incentiveRequestService } from "@/services/incentiveRequestService";
import { REQUEST_CREATED_EVENT } from "@/constants/requestEvents";
import type { IIncentiveRequest } from "@/types/requestTypes";

interface IUseIncentiveRequestsResult {
  requests: IIncentiveRequest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useIncentiveRequests(): IUseIncentiveRequestsResult {
  const [requests, setRequests] = useState<IIncentiveRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState<number>(0);

  const refetch = useCallback((): void => {
    setFetchCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    window.addEventListener(REQUEST_CREATED_EVENT, refetch);
    return (): void => {
      window.removeEventListener(REQUEST_CREATED_EVENT, refetch);
    };
  }, [refetch]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchRequests = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await incentiveRequestService.getMyRequests(abortController.signal);

        if (!abortController.signal.aborted) {
          setRequests(data);
        }
      } catch (err) {
        if (abortController.signal.aborted) {
          return;
        }
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : "Failed to fetch incentive requests");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchRequests();

    return (): void => {
      abortController.abort();
    };
  }, [fetchCount]);

  return { requests, isLoading, error, refetch };
}
